import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Divider, Grid, Card, CardContent, Paper } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RuleIcon from '@mui/icons-material/Rule';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublicIcon from '@mui/icons-material/Public';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SaveIcon from '@mui/icons-material/Save';

import SmartToyIcon from '@mui/icons-material/SmartToy';

import { BreadCrumbsAndTitle } from '../../../components/generics/BreadCrumbsAndTitle';
import { alertInformativo } from '../../../commons/texts';
import { AlertsInformativos } from '../../../components/generics/AlertsInformativos';
import { mtDividerUpTable } from '../../../commons';

import { useDispatch, useSelector } from 'react-redux';
import { mapShiftGeneratorPayload } from './mappers/generadorTurnos.mapper';
import { getPlaningShiftRequestStore, storeGenerateShifts } from '../../../store/shift_generator';
import { BorradorTurnosTable } from './components/BorradorTurnosTable';
import { ScheduleAiAssistant } from './components/ScheduleAiAssistant';
import { useResponseHandler } from '../../../hooks/useResponseHandler';
import { adjustScheduleWithAiStore, storeSaveDraft } from '../../../store/shift_generator/thunks';

export const GeneradorTurnos = () => {
  const dispatch = useDispatch<any>();
  const { lastGeneratedResult, request_shift_list } = useSelector(
    (state: any) => state.shiftGenerator
  );
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [historicalFiles, setHistoricalFiles] = useState<Record<string, File | null>>({});

  const activeRequest = request_shift_list?.find((r: any) => r.id === activeRequestId);
  const activeRequestName = activeRequest?.name || '';

  const { handleResponse, ResponseComponents } = useResponseHandler();

  useEffect(() => {
    getPlaningShiftRequest();
  }, []);

  const getPlaningShiftRequest = async () => {
    const response = await dispatch(getPlaningShiftRequestStore());

    handleResponse(response, 'Generar Turnos', false);
  };

  const handleGenerate = async (request: any) => {
    setActiveRequestId(request.id);

    const payload = mapShiftGeneratorPayload(request);
    const historicalFile = historicalFiles[request.id];

    console.log(
      'Enviando petición a la API vía Redux...',
      payload,
      historicalFile ? 'con archivo histórico' : ''
    );

    const response = await dispatch(storeGenerateShifts(payload, historicalFile));

    await handleResponse(response, 'Generar Turnos', true);

    if (response.ok) {
      console.log('Turnos generados:', response.data);
      // TODO: Mostrar en el borrador de turnos
    }
  };

  const handleAiPrompt = async (prompt: string) => {
    if (!activeRequestId) {
      throw new Error('No hay una solicitud activa seleccionada');
    }
    const response = await dispatch(adjustScheduleWithAiStore(prompt, activeRequestId));
    if (response.ok) {
      return response.message || 'Ajuste completado';
    }
    throw new Error(response.message || 'Error al consultar IA');
  };

  const handleSaveDraft = async () => {
    if (!activeRequestId) {
      return;
    }

    let gridData: any[] = [];
    if (Array.isArray(lastGeneratedResult)) {
      lastGeneratedResult.forEach((res: any) => {
        if (res.grid) {
          gridData = [...gridData, ...res.grid];
        }
      });
    } else if (lastGeneratedResult?.grid) {
      gridData = lastGeneratedResult.grid;
    } else if (lastGeneratedResult?.data?.grid) {
      gridData = lastGeneratedResult.data.grid;
    }

    const assignments: any[] = [];
    gridData.forEach((emp: any) => {
      if (emp.days) {
        Object.entries(emp.days).forEach(([date, dayData]: [string, any]) => {
          if (dayData && dayData.type === 'SHIFT') {
            assignments.push({
              employee_id: emp.id,
              date: date,
              shift_template_id: dayData.shift_template_id || null,
            });
          } else if (dayData && (dayData.type === 'REST' || dayData.type === 'ABSENCE')) {
            assignments.push({
              employee_id: emp.id,
              date: date,
            });
          }
        });
      }
    });

    const payload = {
      requestId: activeRequestId,
      assignments: assignments,
    };

    const response = await dispatch(storeSaveDraft(payload));

    await handleResponse(response, 'Guardar Turnos', true);

    if (response.ok) {
      // Recargar solicitudes para actualizar el estado a publicado/aprobado
      getPlaningShiftRequest();
    }
  };

  return (
    <Box sx={{ pb: 5, width: '100%', p: 1 }}>
      {ResponseComponents}
      <Box sx={{ mb: 3 }}>
        <BreadCrumbsAndTitle
          title="Generador de Turnos"
          mainModule="Talento Humano"
          parent="Programador Inteligente"
          action="Generador"
          icon="AutoAwesome"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <AlertsInformativos
            type={'info'}
            title={'info'}
            content={alertInformativo.generadorTurnos}
          />
        </Typography>
      </Box>

      <Divider sx={{ marginBottom: mtDividerUpTable }} />

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
        Estado de Configuración (Requisitos de IA)
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ p: 1, borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #5DCC0A' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(93, 204, 10, 0.1)', borderRadius: '50%' }}>
                <PublicIcon sx={{ color: '#5DCC0A' }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Reglas Globales
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  Configuradas <CheckCircleIcon sx={{ fontSize: 14, color: '#5DCC0A' }} />
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ p: 1, borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #5DCC0A' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(93, 204, 10, 0.1)', borderRadius: '50%' }}>
                <RuleIcon sx={{ color: '#5DCC0A' }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Reglas por Cargo
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  Configuradas <CheckCircleIcon sx={{ fontSize: 14, color: '#5DCC0A' }} />
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ p: 1, borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #5DCC0A' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(93, 204, 10, 0.1)', borderRadius: '50%' }}>
                <ViewTimelineIcon sx={{ color: '#5DCC0A' }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Plantillas Turnos
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  Configuradas <CheckCircleIcon sx={{ fontSize: 14, color: '#5DCC0A' }} />
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ p: 1, borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #FF4C51' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(255, 76, 81, 0.1)', borderRadius: '50%' }}>
                <PeopleAltIcon sx={{ color: '#FF4C51' }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Req. de Personal
                </Typography>
                <Typography variant="caption" color="error">
                  Revisar Cobertura
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 2, backgroundColor: 'rgb(238, 242, 246)' }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: '#663cc1' }}>
          Requerimientos de Programación Pendientes
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Seleccione un requerimiento para generar automáticamente los horarios óptimos para el
          hotel, cumpliendo todas las reglas y restricciones.
        </Typography>

        {request_shift_list && request_shift_list.length > 0 ? (
          <Grid container spacing={3}>
            {request_shift_list.map((request: any) => {
              const isActive = request.id === activeRequestId;
              return (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={request.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      boxShadow: isActive
                        ? '0 8px 24px rgba(102, 60, 193, 0.25)'
                        : '0 4px 12px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: isActive ? 'scale(1.02) translateY(-2px)' : 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(102, 60, 193, 0.15)',
                      },
                      border: isActive ? '2px solid #e65100' : '1px solid rgba(102, 60, 193, 0.1)',
                      backgroundColor: isActive ? '#fff3e0' : 'white',
                      transform: isActive ? 'scale(1.02)' : 'none',
                      position: 'relative',
                    }}
                  >
                    {isActive && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          backgroundColor: '#e65100',
                        }}
                      />
                    )}
                    <CardContent
                      sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                          <Box
                            sx={{
                              p: 1.5,
                              backgroundColor: isActive ? '#e65100' : 'rgba(102, 60, 193, 0.1)',
                              borderRadius: 2,
                              transition: 'all 0.2s',
                            }}
                          >
                            <EventNoteIcon sx={{ color: isActive ? 'white' : '#663cc1' }} />
                          </Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 'bold', color: isActive ? '#e65100' : '#333' }}
                          >
                            {request.name.split(' | ')[0]}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            backgroundColor:
                              request.statusPlanningRequest === 'PENDING' ? '#fff3e0' : '#e8f5e9',
                            color:
                              request.statusPlanningRequest === 'PENDING' ? '#ef6c00' : '#2e7d32',
                            borderRadius: 5,
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {request.statusPlanningRequest === 'PENDING'
                            ? 'PENDIENTE'
                            : request.statusPlanningRequest}
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography component="span" sx={{ fontWeight: 'bold', color: '#555' }}>
                          Desde:
                        </Typography>{' '}
                        {request.startDate}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography component="span" sx={{ fontWeight: 'bold', color: '#555' }}>
                          Hasta:
                        </Typography>{' '}
                        {request.endDate}
                      </Typography>

                      <Box sx={{ mb: 3 }}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          size="small"
                          sx={{
                            borderColor: historicalFiles[request.id] ? '#2e7d32' : '#ccc',
                            color: historicalFiles[request.id] ? '#2e7d32' : '#666',
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                          }}
                        >
                          {historicalFiles[request.id]
                            ? historicalFiles[request.id]?.name
                            : 'Subir plantilla base (opcional)'}
                          <input
                            type="file"
                            accept=".xlsx, .xls"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              setHistoricalFiles((prev) => ({ ...prev, [request.id]: file }));
                            }}
                          />
                        </Button>
                      </Box>

                      <Button
                        fullWidth
                        variant={isActive ? 'outlined' : 'contained'}
                        onClick={() => handleGenerate(request)}
                        startIcon={isActive ? <CheckCircleIcon /> : <AutoAwesomeIcon />}
                        sx={{
                          marginTop: 'auto',
                          ...(isActive
                            ? {
                                borderColor: '#e65100',
                                color: '#e65100',
                                '&:hover': {
                                  backgroundColor: 'rgba(230, 81, 0, 0.05)',
                                  borderColor: '#bf360c',
                                },
                              }
                            : {
                                backgroundColor: '#663cc1',
                                '&:hover': { backgroundColor: '#522b9c' },
                              }),
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 'bold',
                          py: 1.2,
                        }}
                      >
                        {isActive ? 'Generando / Activo' : 'Generar Turnos'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box
            sx={{
              p: 5,
              textAlign: 'center',
              backgroundColor: 'white',
              borderRadius: 2,
              border: '1px dashed #ccc',
            }}
          >
            <EventNoteIcon sx={{ fontSize: 40, color: '#ccc', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              No hay requerimientos de programación pendientes.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Genera un nuevo requerimiento de personal para poder crear los turnos.
            </Typography>
          </Box>
        )}
      </Paper>

      {Array.isArray(lastGeneratedResult) && lastGeneratedResult.length > 0 ? (
        <Box sx={{ mt: 4 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#663cc1' }}>
              Resultados de Generación
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={handleSaveDraft}
                sx={{
                  borderRadius: 8,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)',
                  },
                }}
              >
                Guardar Turnos
              </Button>
              <Button
                variant="contained"
                startIcon={<SmartToyIcon />}
                onClick={() => setIsAssistantOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #663cc1 0%, #9c27b0 100%)',
                  borderRadius: 8,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(156, 39, 176, 0.4)',
                  },
                }}
              >
                ✨ Ajustar con IA
              </Button>
            </Box>
          </Box>
          {lastGeneratedResult.map((result: any, index: number) => (
            <Box
              key={index}
              sx={{
                mb: 5,
                p: 3,
                backgroundColor: 'white',
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <PeopleAltIcon sx={{ color: '#663cc1' }} />
                Cargo:{' '}
                <span style={{ textTransform: 'capitalize', color: '#555' }}>
                  {result.position_name}
                </span>
              </Typography>

              {result.data?.status === 'failed' && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    backgroundColor: '#fff3f3',
                    borderLeft: '4px solid #ef5350',
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#c62828',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    ⚠️ Conflictos en la Generación
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#c62828', mt: 1 }}>
                    {result.message}
                  </Typography>
                </Box>
              )}

              {result.grid && result.grid.length > 0 ? (
                <BorradorTurnosTable scheduleData={result.grid} />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontStyle: 'italic',
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                  }}
                >
                  No se pudo generar tabla de turnos para este cargo.
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      ) : lastGeneratedResult?.grid || lastGeneratedResult?.data?.grid ? (
        <Box sx={{ mt: 4 }}>
          {lastGeneratedResult?.data?.status === 'failed' && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: '#fff3f3',
                borderLeft: '4px solid #ef5350',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#c62828', fontWeight: 'bold' }}>
                ⚠️ Conflictos en la Generación
              </Typography>
              <Typography variant="body2" sx={{ color: '#c62828', mt: 1 }}>
                {lastGeneratedResult.message}
              </Typography>
            </Box>
          )}
          <BorradorTurnosTable
            scheduleData={lastGeneratedResult?.grid || lastGeneratedResult?.data?.grid}
          />
        </Box>
      ) : (
        <Box
          sx={{
            mt: 5,
            p: 5,
            border: '2px dashed #ccc',
            borderRadius: 3,
            textAlign: 'center',
            backgroundColor: '#fafafa',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 50, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 'bold' }}>
            Área de Borrador de Turnos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aquí aparecerá el calendario interactivo con la propuesta generada por la IA para que
            puedas revisarla antes de publicarla oficialmente.
          </Typography>
        </Box>
      )}

      <ScheduleAiAssistant
        isOpen={isAssistantOpen}
        onClose={() => setIsAssistantOpen(false)}
        activeRequestName={activeRequestName}
        onSendPrompt={handleAiPrompt}
      />
    </Box>
  );
};
